import { autoUpdate, flip, offset, autoPlacement, safePolygon, shift, useClick, useDismiss, useFloating, useFloatingNodeId, useFloatingParentNodeId, useFloatingTree, useHover, useInteractions, useListNavigation, useRole } from '@floating-ui/react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MenuContext } from "../../context/MenuContext.js";
import { SearchContext } from "../../context/SearchContext.js";
import { SettingsContext } from "../../context/SettingsContext.js";
import { getDefaultFloatingOptions } from "../../functions/getDefaultFloatingOptions.js";
import { useIsNested } from "../../hooks/useIsNested.js";
import { useMobileCheck } from "../../hooks/useMobileCheck.js";
import { jsx as _jsx } from "react/jsx-runtime";
export function MenuProvider({
  virtual,
  level,
  children
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isChildOpen, setIsChildOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const isMobile = useMobileCheck();
  const tree = useFloatingTree();
  const nodeId = useFloatingNodeId();
  const parentId = useFloatingParentNodeId();
  const virtualItemRef = useRef(null);
  const listRef = useRef([]);
  const labelsRef = useRef([]);
  const isNested = useIsNested();
  const parent = useContext(MenuContext);
  const {
    getOffsetOptions = getDefaultFloatingOptions,
    getAutoPlacement
  } = useContext(SettingsContext);
  const {
    setIsChildOpen: setIsChildOpenForParent
  } = parent;
  const {
    filteredItems,
    setSearchString
  } = useContext(SearchContext);

  // Update parent isOpen when value changes
  useEffect(() => {
    setIsChildOpenForParent(isOpen);
  }, [isOpen, setIsChildOpenForParent]);

  // Reset search string if current menu closes
  useEffect(() => {
    if (!isOpen) {
      setSearchString('');
    }
  }, [isOpen, setSearchString]);
  const middleware = useMemo(() => {
    const middlewareArray = [offset(getOffsetOptions({
      isNested
    })), shift()];
    if (getAutoPlacement) {
      middlewareArray.push(autoPlacement(getAutoPlacement({
        isNested
      })));
    } else {
      middlewareArray.push(flip());
    }
    return middlewareArray;
  }, [getOffsetOptions, getAutoPlacement, isNested]);

  // Init floating
  const {
    floatingStyles,
    refs,
    context
  } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: isNested ? 'right-start' : 'bottom-start',
    middleware,
    whileElementsMounted: autoUpdate
  });

  // Init contextMenu list navigation
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    nested: isNested,
    onNavigate: setActiveIndex,
    virtual,
    virtualItemRef
  });

  // Init contextMenu interaction
  const hover = useHover(context, {
    enabled: isNested && !isMobile,
    delay: {
      open: 75
    },
    handleClose: safePolygon({
      blockPointerEvents: true
    })
  });
  const click = useClick(context, {
    event: 'mousedown',
    toggle: !isNested,
    ignoreMouse: isNested && !isMobile
  });
  const role = useRole(context, {
    role: 'menu'
  });
  const dismiss = useDismiss(context, {
    bubbles: true,
    outsidePress: e => {
      // Need type cast because floating-ui has typing issues with virtual elements
      const buttonReference = refs.reference.current?.contextElement;
      // Need this check because otherwise the click on the reference button will cause a dismiss close and instantly re-open because of it's own toggle behavior
      return !(e.target === buttonReference || buttonReference?.contains(e.target));
    }
  });
  const {
    getReferenceProps,
    getFloatingProps,
    getItemProps
  } = useInteractions([hover, click, role, dismiss, listNavigation]);

  // Close menu on tree click
  useEffect(() => {
    if (!tree) {
      return;
    }
    function handleTreeClick() {
      setIsOpen(false);
    }
    tree.events.on('click', handleTreeClick);
    return () => {
      tree.events.off('click', handleTreeClick);
    };
  }, [tree, nodeId, parentId]);

  // Reset activeIndex when filtered items change
  useEffect(() => {
    setActiveIndex(null);
  }, [filteredItems]);
  const value = useMemo(() => ({
    getItemProps,
    getFloatingProps,
    getReferenceProps,
    activeIndex,
    setActiveIndex,
    isOpen,
    setIsOpen,
    isChildOpen,
    setIsChildOpen,
    nodeId,
    refs,
    context,
    floatingStyles,
    parent,
    listRef,
    labelsRef,
    level,
    virtualItemRef
  }), [getItemProps, getFloatingProps, getReferenceProps, activeIndex, setActiveIndex, isOpen, setIsOpen, isChildOpen, setIsChildOpen, nodeId, refs, context, floatingStyles, parent, listRef, labelsRef, level, virtualItemRef]);
  return /*#__PURE__*/_jsx(MenuContext.Provider, {
    value: value,
    children: children
  });
}